import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { CreatePrincipalModal } from 'components/PrincipalModals/CreatePrincipalModal';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../../components/Heading';
import { Link } from '../../../components/Link';
import { EditPrincipalModal } from '../../../components/PrincipalModals/EditPrincipalModal';
import { Table } from '../../../components/Table/Table';
import { TableBody } from '../../../components/Table/TableBody';
import { TableCell } from '../../../components/Table/TableCell';
import { TableHeader } from '../../../components/Table/TableHeader';
import { TableRow } from '../../../components/Table/TableRow';
import { routes } from '../../../controllers/ContentController/Routes';
import { usePrincipals } from '../../../hooks/queries';
import { getCurrentPrincipal } from '../../../lib/currentPrincipal';
import {
  PrimaryFeature,
  Principal,
  PrincipalModalTypes,
} from '../../../utils/types';
import { PrincipalTableRow } from './PrincipalsTableRow';

export const PrincipalAdministration: React.FC = () => {
  const { t } = useTranslation('translation');
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);
  const navigate = useNavigate();
  const adminCenterRoute = routes(currentPrincipal.slug).find(
    PrimaryFeature.ADMIN_CENTER,
  );

  const [targetPrincipal, setTargetPrincipal] = useState<Principal | null>(
    null,
  );
  const [activePrincipalModal, setActivePrincipalModal] =
    useState<PrincipalModalTypes | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>();

  const onPrincipalSelection = (principalId: string) => {
    const chosenPrincipal = principals?.find(({ id }) => id === principalId);
    if (!chosenPrincipal) return;

    setTargetPrincipal(chosenPrincipal);
    setActivePrincipalModal(PrincipalModalTypes.EDIT);
  };

  return (
    <div className="w-full">
      <div className="max-w-xl-content xl:min-w-content mx-auto mb-7 space-y-5 px-4 pt-6 md:mb-9 md:px-6 md:pt-7">
        <div className="mb-2">
          <Link
            label={t('linksOrButtons:back')}
            icon="ArrowLeft"
            iconPosition="left"
            onClick={() => navigate(adminCenterRoute.url)}
          />
        </div>

        <div className="space-y-4">
          <div className="mt-1 flex flex-row space-x-4">
            <div className="w-3/5">
              <Heading
                text={t(
                  'features:admin-center:principalAdministration:tile:title',
                )}
                margin={0}
                level="h1"
              />
              <div className="text-grey-700 mt-5">
                {t('features:admin-center:principalAdministration:description')}
              </div>
            </div>
            <div className="w-2/5 text-right">
              <Button
                onClick={() =>
                  setActivePrincipalModal(PrincipalModalTypes.CREATE)
                }
              >
                <div className="flex flex-row justify-center">
                  <span className="mr-1 inline-block h-3 w-3">
                    <Icon name="Add" inline={true} />
                  </span>
                  <span>
                    {t(
                      'features:admin-center:principalAdministration:createPrincipalModal:button',
                    )}
                  </span>
                </div>
              </Button>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow darkBorder>
                <TableCell
                  noWrap
                  displaySortingButton={true}
                  sortOrder={sortOrder}
                  onClick={() =>
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                  }
                >
                  {t('features:admin-center:principalAdministration:principal')}
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {principals
                ?.sort(({ name: a }, { name: b }) =>
                  sortOrder === 'desc'
                    ? -a.localeCompare(b)
                    : a.localeCompare(b),
                )
                .map((p: Principal) => (
                  <PrincipalTableRow
                    key={p.id}
                    principalId={p.id}
                    logoUrl={p.logoUrl}
                    principalName={p.name}
                    onPrincipalSelection={onPrincipalSelection}
                  />
                ))}
            </TableBody>
          </Table>
        </div>
      </div>
      {activePrincipalModal === PrincipalModalTypes.EDIT && targetPrincipal && (
        <EditPrincipalModal
          onClose={() => {
            setActivePrincipalModal(null);
            setTargetPrincipal(null);
          }}
          principalId={targetPrincipal.id}
        />
      )}
      {activePrincipalModal === PrincipalModalTypes.CREATE && (
        <CreatePrincipalModal
          onClose={() => {
            setActivePrincipalModal(null);
          }}
        />
      )}
    </div>
  );
};
